import { useEffect, useState } from "react";
import type { StreamFrameRate, StreamResolution } from "@atrium/shared";
import { Dialog } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { Segmented, SettingRow, Switch } from "../../components/ui/Controls";
import { IconScreenShare } from "../../components/icons";
import { useStreamUi } from "./stores";
import { loadStreamChoice, type StreamChoice } from "./stream";
import { useVoice } from "./VoiceProvider";

const RESOLUTIONS: { value: StreamResolution; label: string }[] = [
  { value: "720", label: "720p" },
  { value: "1080", label: "1080p" },
  { value: "source", label: "Source" },
];
const FRAME_RATES: { value: `${StreamFrameRate}`; label: string }[] = [
  { value: "15", label: "15 fps" },
  { value: "30", label: "30 fps" },
  { value: "60", label: "60 fps" },
];

/**
 * Stream quality before going live (and while live). Choosing what to share
 * happens in the browser's own picker, which opens from the Go live button.
 */
export function GoLiveDialog() {
  const mode = useStreamUi((s) => s.goLive);
  const open = useStreamUi((s) => s.openGoLive);
  const voice = useVoice();
  const [choice, setChoice] = useState<StreamChoice>(loadStreamChoice);
  const editing = mode === "edit";

  useEffect(() => {
    if (!mode) return;
    const saved = loadStreamChoice();
    setChoice(mode === "edit" && voice.shareSettings ? { ...voice.shareSettings, audio: voice.shareHasAudio ? voice.shareSettings.audio : saved.audio } : saved);
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live state can end underneath the dialog (stopped from the browser bar, or kicked).
  useEffect(() => {
    if (editing && !voice.shareSettings) open(null);
    if (mode === "start" && voice.status !== "connected") open(null);
  }, [editing, mode, voice.shareSettings, voice.status, open]);

  const submit = () => {
    open(null);
    // Straight from the click: the browser only opens its picker during a user gesture.
    if (editing) void voice.updateShare(choice);
    else void voice.startShare(choice);
  };

  const audioLocked = editing && !voice.shareHasAudio;
  return (
    <Dialog
      open={!!mode}
      onOpenChange={(o) => !o && open(null)}
      size="sm"
      title={editing ? "Stream quality" : "Share your screen"}
      description={editing ? "Changes apply straight away." : "Your browser will ask which screen, window or tab to share."}
      footer={
        <>
          <Button variant="ghost" onClick={() => open(null)}>
            Cancel
          </Button>
          <Button variant="primary" icon={editing ? undefined : IconScreenShare} onClick={submit} data-autofocus>
            {editing ? "Apply" : "Go live"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-2">
            Resolution
          </span>
          <Segmented ariaLabel="Resolution" value={choice.resolution} onChange={(resolution) => setChoice((c) => ({ ...c, resolution }))} options={RESOLUTIONS} />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-2">Frame rate</span>
          <Segmented
            ariaLabel="Frame rate"
            value={`${choice.fps}` as `${StreamFrameRate}`}
            onChange={(v) => setChoice((c) => ({ ...c, fps: Number(v) as StreamFrameRate }))}
            options={FRAME_RATES}
          />
          <p className="text-sm text-fg-3">{choice.fps >= 60 ? "Smoothest for games and video." : "Keeps text and detail sharp."} Higher quality uses more upload for each viewer.</p>
        </div>
        <div className="-my-3">
          <SettingRow
            title="Include sound"
            htmlFor="go-live-audio"
            description={audioLocked ? "What you're sharing has no sound. Change source and tick Share audio to add it." : "Tab sound, or system sound when sharing a whole screen where supported."}
          >
            <Switch id="go-live-audio" label="Include sound" checked={choice.audio && !audioLocked} disabled={audioLocked} onCheckedChange={(audio) => setChoice((c) => ({ ...c, audio }))} />
          </SettingRow>
        </div>
      </div>
    </Dialog>
  );
}
